import * as THREE from 'three'
import { appearanceOf } from '@/utils/meshAppearance'
import { TINT_EMISSIVE_INTENSITY } from '@/utils/materialTint'

/** Meshes drawn through a batch move to this layer, so the camera skips them but picking does not. */
export const BATCHED_LAYER = 1

// Per-instance colour alpha carries the opacity, plus 2 when the instance is tinted, so a tint can
// also glow the way setMaterialTint makes a single material glow.
const TINT_FLAG = 2

const BATCHED_COLOR_FRAGMENT = /* glsl */ `
#ifdef USE_COLOR_ALPHA
  float batchTint = step( 1.5, vColor.a );
  diffuseColor *= vec4( vColor.rgb, vColor.a - ${TINT_FLAG.toFixed(1)} * batchTint );
#endif
`
const BATCHED_EMISSIVE_FRAGMENT = /* glsl */ `
#include <emissivemap_fragment>
#ifdef USE_COLOR_ALPHA
  totalEmissiveRadiance += vColor.rgb * batchTint * ${TINT_EMISSIVE_INTENSITY.toFixed(2)};
#endif
`

type Slot = {
  mesh: THREE.Mesh
  opaque: THREE.BatchedMesh
  dimmed: THREE.BatchedMesh
  instanceId: number
  authoredColor: THREE.Color
}

function batchMaterial(base: THREE.MeshStandardMaterial, dimmed: boolean) {
  const material = base.clone()
  material.color.set(0xffffff)
  material.emissive.set(0x000000)
  if (dimmed) {
    material.transparent = true
    material.depthWrite = false
  }
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <color_fragment>', BATCHED_COLOR_FRAGMENT)
      .replace('#include <emissivemap_fragment>', BATCHED_EMISSIVE_FRAGMENT)
  }
  return material
}

// The dimmed copy draws the same vertex data, so it shares the opaque batch's buffers.
function copySharingGeometry(source: THREE.BatchedMesh, material: THREE.Material) {
  const geometry = source.geometry
  source.geometry = new THREE.BufferGeometry()
  const copy = new THREE.BatchedMesh(1, 1, 1).copy(source)
  source.geometry = geometry
  copy.geometry.dispose()
  copy.geometry = geometry
  copy.material = material
  return copy
}

function materialKey(m: THREE.MeshStandardMaterial): string {
  return [
    m.type,
    m.side,
    m.transparent,
    m.opacity,
    m.alphaTest,
    m.metalness,
    m.roughness,
    m.envMapIntensity,
    m.flatShading,
    m.vertexColors,
    m.wireframe,
    m.depthTest,
    m.depthWrite,
    m.blending,
    m.map?.uuid,
    m.normalMap?.uuid,
    m.roughnessMap?.uuid,
    m.metalnessMap?.uuid,
    m.aoMap?.uuid,
    m.alphaMap?.uuid,
    m.emissiveMap?.uuid,
  ].join('|')
}

function batchKey(mesh: THREE.Mesh): string | null {
  const material = mesh.material
  const geometry = mesh.geometry
  if (!(material instanceof THREE.MeshStandardMaterial) || !geometry.index) return null
  const attributes = Object.entries(geometry.attributes)
    .map(([name, a]) => `${name}:${a.itemSize}:${a.normalized}:${a.array.constructor.name}`)
    .sort()
  return `${materialKey(material)}|${attributes.join(',')}`
}

/**
 * Draws a loaded model in a few multi-draw calls instead of one call per mesh: a STEP assembly
 * easily has thousands of meshes, and the per-draw overhead made orbiting it stutter. The meshes
 * stay in the scene for picking, bounds and transforms; hovered and selected ones are drawn
 * individually since their highlight is not expressible per instance.
 */
export class MeshBatches {
  readonly root = new THREE.Group()
  private readonly slots: Slot[] = []
  private readonly color = new THREE.Color()
  private readonly instanceColor = new THREE.Vector4()

  constructor(meshes: THREE.Mesh[]) {
    const groups = new Map<string, THREE.Mesh[]>()
    for (const mesh of meshes) {
      const key = batchKey(mesh)
      if (!key) continue
      const group = groups.get(key)
      if (group) group.push(mesh)
      else groups.set(key, [mesh])
    }

    for (const group of groups.values()) {
      const base = group[0].material as THREE.MeshStandardMaterial
      const geometries = [...new Set(group.map((m) => m.geometry))]
      const vertexCount = geometries.reduce((sum, g) => sum + g.attributes.position.count, 0)
      const indexCount = geometries.reduce((sum, g) => sum + g.index!.count, 0)

      const opaque = new THREE.BatchedMesh(
        group.length,
        vertexCount,
        indexCount,
        batchMaterial(base, false),
      )
      const geometryIds = new Map(geometries.map((g) => [g, opaque.addGeometry(g)]))
      const instanceIds = group.map((m) => opaque.addInstance(geometryIds.get(m.geometry)!))
      opaque.sortObjects = false

      const dimmed = copySharingGeometry(opaque, batchMaterial(base, true))
      for (const batch of [opaque, dimmed]) {
        batch.frustumCulled = false
        this.root.add(batch)
      }

      group.forEach((mesh, i) => {
        mesh.layers.set(BATCHED_LAYER)
        this.slots.push({
          mesh,
          opaque,
          dimmed,
          instanceId: instanceIds[i],
          authoredColor: (mesh.material as THREE.MeshStandardMaterial).color.clone(),
        })
      })
    }
  }

  /** Mirrors each mesh's visibility, look and world matrix onto its batch instance. */
  sync() {
    for (const { mesh, opaque, dimmed, instanceId, authoredColor } of this.slots) {
      const look = appearanceOf(mesh)
      const individual = look.hovered || look.selected
      mesh.layers.set(individual ? 0 : BATCHED_LAYER)

      const shown = mesh.visible && !individual
      const isDimmed = look.opacity < 1
      opaque.setVisibleAt(instanceId, shown && !isDimmed)
      dimmed.setVisibleAt(instanceId, shown && isDimmed)
      if (!shown) continue

      const batch = isDimmed ? dimmed : opaque
      if (look.tint === null) this.color.copy(authoredColor)
      else this.color.setHex(look.tint)
      const alpha = look.opacity + (look.tint === null ? 0 : TINT_FLAG)
      batch.setColorAt(
        instanceId,
        this.instanceColor.set(this.color.r, this.color.g, this.color.b, alpha),
      )
      batch.setMatrixAt(instanceId, mesh.matrixWorld)
    }
  }

  dispose() {
    for (const batch of this.root.children as THREE.BatchedMesh[]) {
      ;(batch.material as THREE.Material).dispose()
      batch.dispose()
    }
    for (const { mesh } of this.slots) mesh.layers.set(0)
    this.root.removeFromParent()
  }
}
